import { Injectable } from '@angular/core';

import {
  each,
  extend,
  filter,
  find,
  findIndex,
  get,
  isArray,
  isEmpty,
  isString,
  map as mapL,
  remove,
  toLower,
  cloneDeep
} from 'lodash';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';
import * as ipaddr from 'ipaddr.js';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { Site } from './sites.model.factory';
import { IEntity, IEntityCache, TimeMode } from '../../providers/inputs.datastore/types';
import { ApiService } from '../../providers/api/api.service';
import { InputDatastoreService } from '../../providers/inputs.datastore/inputs.datastore.service';
import { LoggerService } from '../../providers/logger/logger.service';
import { NotificationService } from '../../components/notifications/notifications.service';
import { ISite, getSiteByIPRequest, IApexSiteSync } from '../../models/types';
import { TimestampRange } from '../../components/time-picker/types';

@Injectable({
  providedIn: 'root'
})
export class SitesService implements IEntityCache {
  deltaTimeFrom: number;
  historicalmonitorcache: Array<Site>;
  public realtimemonitorcache: Array<Site>;
  cache: BehaviorSubject<Array<Site>>;
  currentMode: TimeMode = 'real-time';
  isManualMethodType = new BehaviorSubject<boolean>(false);
  queryLoading = false;
  updated: Subject<Array<Site>>;
  initialQuery = false;
  overallSites: Array<Site> = [];
  private readonly unsubscribe: Subject<void> = new Subject();
  private readonly unsubscribeUS: Subject<void> = new Subject();
  private readonly CREATE_SITE_WIZARD_ID = 'add_sites';
  private readonly UPDATE_SITE_WIZARD_ID = 'update_sites';
  private readonly CREATE_AGENT_WIZARD_ID = 'add_agent';
  private sitesPostURL = '/entity/v1/sites/findAll';
  private getSiteByIPURL = '/entity/v1/sites/findByIP';
  private getApexSiteURL = '/entity/v1/sync';
  private sitePostParams = {
    size: 2000,
    includeDeleted: true,
    sort: 'id',
    page: 0
  };
  config: any; // eslint-disable-line

  constructor(
    private readonly apiService: ApiService,
    private readonly inputDatastoreService: InputDatastoreService,
    private readonly navigator: NavigationProvider,
    private readonly logging: LoggerService,
    private readonly notificationService: NotificationService
  ) {
    this.deltaTimeFrom = null;
    this.realtimemonitorcache = [];
    this.historicalmonitorcache = [];

    this.cache = new BehaviorSubject([]);
  }

  getEntityName(): string {
    return 'site';
  }

  findById(id: string): IEntity {
    return this.getSitesById(id);
  }

  findByName(name: string): IEntity {
    return find(this.getSites(), site => site.getName() === name);
  }

  getByName(name: string): IEntity[] {
    return filter(this.getSites(), site => site.getName() === name);
  }

  findByEscapedName(name: string): IEntity {
    return find(this.getSites(), app => app.getEscapedName() === name);
  }

  getByEscapedName(name: string): IEntity[] {
    name = toLower(name);
    // we need to support case insensitive match
    return filter(this.getSites(), app => toLower(app.getEscapedName()) === name);
  }

  // eslint-disable-next-line
  getValues(): any[] {
    return this.getSites();
  }

  // eslint-disable-next-line
  getValuesAsync(): Observable<Array<any>> {
    const data = this.getValues();
    if (!data || data.length === 0) {
      return this.updateSites(this.realtimemonitorcache).pipe(first());
    }
    return of(data).pipe(first());
  }

  getSites(): Array<Site> {
    return this.chooseCacheModel(this.currentMode);
  }

  // return an observable site list where sites has not been deleted
  // eslint-disable-next-line
  getActiveSitesAsync(): Observable<Array<any>> {
    return this.getValuesAsync().pipe(
      map(arr => arr.filter(site => site.sitesData && !site.sitesData.deleted))
    );
  }

  // return sites that have not been deleted
  getActiveSites(): ISite[] {
    const cachedSites = this.getSites();
    const filteredData = cachedSites.map(s => s.sitesData);
    return filteredData.filter(site => !site.deleted);
  }

  getSitesById(siteId: string): Site {
    return find(this.getSites(), site => site.getId() == siteId);
  }

  getSiteByIP(ip: string): ISite {
    const addr: any = ipaddr.parse(ip); // eslint-disable-line
    const definedSites = this.getActiveSites().filter(s => s.id >= 10000);
    return find(definedSites, site => {
      const mysub = find(site.subnets, subnet => {
        try {
          return addr.match(ipaddr.parseCIDR(subnet.subnet));
        } catch (err) {
          return false;
        }
      });
      return !!mysub;
    });
  }

  dispose(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribeUS.next();
    this.unsubscribeUS.complete();
  }

  updateSites(
    cache: Array<Site>,
    params?: TimestampRange, // eslint-disable-line
    firstRequest?: boolean, // eslint-disable-line
    mode?: TimeMode, // eslint-disable-line
    sitePostParams?: any // eslint-disable-line
    // eslint-disable-next-line
  ): Observable<any> {
    if (this.queryLoading) {
      return this.updated;
    }
    this.updated = new Subject();
    this.queryLoading = true;
    this.initialQuery = false;
    const siteParams = cloneDeep(sitePostParams ? sitePostParams : this.sitePostParams);
    this.apiService
      .post$<any>(this.sitesPostURL, siteParams) // eslint-disable-line
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (response && isArray(response.content)) {
            if (response.first) {
              // First api call clear the monitors array
              this.overallSites = [];
            }
            if (cache.length === 0) {
              this.initialQuery = true;
            }
            this.queryLoading = false;
            const responseData = this.transformResponse(response.content);
            this.overallSites.push(...responseData);
            this.assignSiteToCacheList(cache, responseData);
            this.updated.next(this.getSites());
            // return this.chooseCacheModel(this.currentMode);
            this.paginateSites(response.last, siteParams);
          }
        },
        // eslint-disable-next-line
        error => {
          this.queryLoading = false;
        },
        () => {
          this.queryLoading = false;
          this.updated.complete();
        }
      );
    return this.updated;
  }

  // eslint-disable-next-line
  transformResponse(dataIn: any): Array<Site> {
    // eslint-disable-line
    dataIn = isString(dataIn) ? JSON.parse(dataIn) : dataIn;
    const response = [];
    if (dataIn !== null && isArray(dataIn)) {
      dataIn.forEach(result => {
        const configIn = {} as ISite;
        extend(configIn, result);
        const siteObj: Site = new Site(configIn);
        // Apex sites will have location in the description field
        const locationName =
          this.populateLocationName(siteObj.getLocation()) || siteObj.getDescription();
        siteObj.setLocationName(locationName);
        response.push(siteObj);
      });
    }
    return response.sort((siteA, siteB) => {
      const name1 = siteA.escapedName.toLowerCase();
      const name2 = siteB.escapedName.toLowerCase();
      return name1 < name2 ? -1 : name1 > name2 ? 1 : 0;
    });
  }

  assignSiteToCacheList(cache: Array<Site>, data: Array<Site>): void {
    cache.length = 0;
    for (const entry of this.overallSites) {
      cache.push(entry);
    }
  }

  removeSiteCacheByID(cache: Array<Site>, siteId: string): Array<Site> {
    return remove(cache, site => site.getId() === siteId);
  }

  findSiteByIndex(cache: Array<Site>, index: number): Site {
    return get(cache, `[${index}]`, null);
  }

  chooseCacheModel(mode?: TimeMode): Array<Site> {
    if (mode === 'historical') {
      return this.historicalmonitorcache;
    } else {
      return this.realtimemonitorcache;
    }
  }

  // eslint-disable-next-line
  populateLocationName(location: any): string {
    let locationFullName = '';
    if (location) {
      locationFullName += location.city ? location.city + ', ' : '';
      locationFullName += location.state ? location.state + ', ' : '';
      locationFullName += location.country ? location.country : '';
    }
    return locationFullName;
  }

  findIndexById(cache: Array<Site>, uid: string): number {
    return findIndex(cache, site => uid === site.getId());
  }

  clearRealtimeCache(): void {
    this.realtimemonitorcache.length = 0;
  }

  findSiteCacheByID(cache: Array<Site>, siteId: string): Site {
    return find(cache, site => site.getId() === siteId);
  }

  // eslint-disable-next-line
  removeSites$(url: any, params: any): Observable<any> {
    return this.apiService.post$<any>(url, params); // eslint-disable-line
  }

  // eslint-disable-next-line
  removeSitesResponse(response: any): void {
    const result: Array<Site> = response;
    if (response && response.error) {
      const errorMsg = 'There is problem while removing an site';
      this.notificationService.onFail(errorMsg);
    } else if (!isEmpty(result)) {
      const sitesToRemove = mapL(result, 'success.id');
      this.removeSitesFromCache(sitesToRemove);
      const successMsg = 'Site removed successfully';
      this.notificationService.onSuccess(successMsg);
    }
  }

  removeSitesFromCache(data: Array<string>): void {
    const allSites = this.getSites();
    each(data, id => {
      this.removeOneSite(allSites, id);
    });

    this.cache.next(this.chooseCacheModel(this.currentMode));
  }

  removeOneSite(allSites: Array<Site>, id: string): void {
    this.removeSiteCacheByID(allSites, id);
  }

  // Clear each page widget component queue
  clearQueues(): void {
    this.inputDatastoreService.clearQueues();
  }

  //Edit Site Details
  editSite(site: Site): void {
    const siteId = get(site, 'id');
    const outletParams = {
      wizard: this.UPDATE_SITE_WIZARD_ID
    };
    this.launchWizard(outletParams, { dataId: siteId });
  }

  addSite(): void {
    const outletParams = {
      wizard: this.CREATE_SITE_WIZARD_ID
    };
    this.launchWizard(outletParams, {});
  }

  addAgent(site: ISite): void {
    const defaultParams = {
      siteName: site.name,
      siteId: site.id,
      location: {
        latitude: site.location.lat,
        longitude: site.location.lon,
        city: site.location.city,
        country: site.location.country
      }
    };
    this.navigator.navigateToWizard([this.CREATE_AGENT_WIZARD_ID], {
      queryParams: {
        defaultParams: JSON.stringify(defaultParams)
      }
    });
  }

  // eslint-disable-next-line
  launchWizard(outletParams: any, queryParams: any): void {
    this.navigator.navigateToWizard(outletParams['wizard'], {
      queryParams: queryParams
    });
  }

  public getSiteIDsByIPAddress(ips: Array<string>): Observable<getSiteByIPRequest[]> {
    return this.apiService
      .post$<any>(this.getSiteByIPURL, ips) // eslint-disable-line
      .pipe(takeUntil(this.unsubscribe));
  }

  public getAllApexSync(): Observable<IApexSiteSync[]> {
    return this.apiService.get$<any>(this.getApexSiteURL); // eslint-disable-line
  }

  public updateApexSyncStatus(params: {
    apexId: string;
    status: string;
  }): Observable<{ status: string; message: string }> {
    // eslint-disable-next-line
    return this.apiService.post$<any>(this.getApexSiteURL + '/active', params);
  }

  // eslint-disable-next-line
  public removeApexSync(apexId: string): Observable<{ status: string; message: string }> {
    // eslint-disable-next-line
    return this.apiService.delete$<any>(this.getApexSiteURL + '/' + apexId);
  }

  //eslint-disable-next-line
  paginateSites(isLast: boolean, params: any): void {
    if (!isLast) {
      if (this.initialQuery) {
        //Update the cache immediately when first response came back
        this.cache.next(this.chooseCacheModel(this.currentMode));
      }
      params.page = params.page + 1;
      this.updateSites(this.getSites(), null, null, null, params);
    } else {
      //update the cache once all scroll api completed
      this.cache.next(this.chooseCacheModel(this.currentMode));
    }
  }
}
